import { Pencil, Save } from "lucide-react";
import { Button } from "../../../../../../components/ui/common/button";
import { FormInputNumber } from "../../../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { PackagingType } from "../../../../../../config/enums/PackagingType.enum";
import { validateCultivationNumber } from "../../../../../../utils/utils";
import { PackagingLevelUnitSectionProps } from "../packagingLevel.types";
import PackagingTotalWeightBadge from "./PackagingTotalWeightBadge";
import PackagingWeightInput from "./PackagingWeightInput";

const numberValidation = {
  validate: {
    valueCheck: (v: any) => validateCultivationNumber(v)
  }
};

const inlineUnitFieldClass = "[&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0";
const inlineUnitRowClass = "tw-flex tw-gap-2 tw-items-end tw-flex-wrap";

const PackagingLevelUnitSection = ({
  packagingType,
  fieldName,
  levelMeta,
  control,
  unitFieldsLocked,
  showMaterialsSection,
  unitGateMessage,
  lockStatus,
  onUnitEdit,
  saveUnitValue,
  saveTransportUnitValue,
  canSavePackagingUnit,
  canSaveTransportUnit,
  palletUnitNumeric,
  palletWidthData,
  palletLengthData,
  unitSizes,
  palletLength,
  palletWidth,
  weightUnit,
  unitSizeWeightUnit,
  onUnitSizeWeightUnitChange,
  totalWeightLabel,
  displayTotalWeight,
  totalWeightUnit
}: PackagingLevelUnitSectionProps) => {
  const weightBadge = showMaterialsSection ? (
    <PackagingTotalWeightBadge
      label={totalWeightLabel}
      value={displayTotalWeight}
      unit={totalWeightUnit}
    />
  ) : null;

  return (
    <>
      <div className="tw-space-y-1.5">
        {packagingType !== PackagingType.TRANSPORT_UNIT_PACKAGING && (
          <label className="tw-text-sm tw-font-medium tw-text-foreground">
            {levelMeta.label}
            <span className="tw-text-destructive tw-ml-0.5">*</span>
          </label>
        )}

        {packagingType === PackagingType.SINGLE_UNIT_PACKAGING ? (
          <div className="tw-flex tw-items-end tw-justify-between tw-gap-4 tw-w-full tw-flex-wrap">
            <div className={inlineUnitRowClass}>
              <PackagingWeightInput
                id={fieldName}
                control={control}
                name={fieldName}
                weightUnit={unitSizeWeightUnit ?? weightUnit}
                onUnitChange={value => onUnitSizeWeightUnitChange?.(value)}
                disabled={unitFieldsLocked || lockStatus}
                placeholder="Enter unit size"
              />
              {!unitFieldsLocked && (
                <Button
                  type="button"
                  size="sm"
                  className="!tw-h-10 tw-gap-1"
                  disabled={!canSavePackagingUnit}
                  onClick={() => saveUnitValue(palletUnitNumeric)}
                >
                  <Save className="tw-h-3.5 tw-w-3.5" />
                  {unitSizes > 0 ? "Update" : "Save"}
                </Button>
              )}
              {unitFieldsLocked && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="!tw-h-10 tw-gap-1 tw-text-xs"
                  disabled={lockStatus}
                  onClick={onUnitEdit}
                >
                  <Pencil className="tw-h-3 tw-w-3" /> Edit
                </Button>
              )}
            </div>
            {weightBadge}
          </div>
        ) : packagingType === PackagingType.TRANSPORT_UNIT_PACKAGING ? (
          <div className="tw-space-y-3">
            <div className="tw-flex tw-items-end tw-justify-between tw-gap-4 tw-flex-wrap">
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-[1fr_1fr_1fr_auto] tw-gap-4 md:tw-items-end tw-flex-1">
                <FormInputNumber
                  control={control}
                  name={fieldName}
                  label="Number of Cases"
                  required
                  placeholder="Enter number"
                  disabled={unitFieldsLocked || lockStatus}
                  rules={numberValidation}
                  id={fieldName}
                />
                <FormInputNumber
                  control={control}
                  name="palletWidth"
                  label="Pallet Width (cm)"
                  required
                  placeholder="e.g. 80"
                  disabled={unitFieldsLocked || lockStatus}
                  rules={numberValidation}
                />
                <FormInputNumber
                  control={control}
                  name="palletLength"
                  label="Pallet Length (cm)"
                  required
                  placeholder="e.g. 120"
                  disabled={unitFieldsLocked || lockStatus}
                  rules={numberValidation}
                />
                {!unitFieldsLocked && (
                  <Button
                    type="button"
                    size="sm"
                    className="!tw-h-10 tw-gap-1 tw-self-end"
                    disabled={!canSaveTransportUnit}
                    onClick={() =>
                      saveTransportUnitValue(palletWidthData, palletLengthData, palletUnitNumeric)
                    }
                  >
                    <Save className="tw-h-3.5 tw-w-3.5" />
                    {unitSizes > 0 || (palletLength ?? 0) > 0 || (palletWidth ?? 0) > 0
                      ? "Update"
                      : "Save"}
                  </Button>
                )}
                {unitFieldsLocked && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="!tw-h-10 tw-gap-1 tw-text-xs tw-self-end"
                    disabled={lockStatus}
                    onClick={onUnitEdit}
                  >
                    <Pencil className="tw-h-3 tw-w-3" /> Edit
                  </Button>
                )}
              </div>
              {weightBadge}
            </div>
          </div>
        ) : (
          <div className="tw-flex tw-items-end tw-justify-between tw-gap-4 tw-w-full tw-flex-wrap">
            <div className={inlineUnitRowClass}>
              <div className={`tw-max-w-xs ${inlineUnitFieldClass}`}>
                <FormInputNumber
                  control={control}
                  name={fieldName}
                  label=""
                  required
                  placeholder="Enter number"
                  disabled={unitFieldsLocked || lockStatus}
                  rules={numberValidation}
                  id={fieldName}
                />
              </div>
              {!unitFieldsLocked && (
                <Button
                  type="button"
                  size="sm"
                  className="!tw-h-10 tw-gap-1"
                  disabled={!canSavePackagingUnit}
                  onClick={() => saveUnitValue(palletUnitNumeric)}
                >
                  <Save className="tw-h-3.5 tw-w-3.5" />
                  {unitSizes > 0 ? "Update" : "Save"}
                </Button>
              )}
              {unitFieldsLocked && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="!tw-h-10 tw-gap-1 tw-text-xs"
                  disabled={lockStatus}
                  onClick={onUnitEdit}
                >
                  <Pencil className="tw-h-3 tw-w-3" /> Edit
                </Button>
              )}
            </div>
            {weightBadge}
          </div>
        )}
      </div>

      {!showMaterialsSection && (
        <p className="tw-text-xs tw-text-muted-foreground tw-italic">{unitGateMessage}</p>
      )}
    </>
  );
};

export default PackagingLevelUnitSection;
